import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Clock, ExternalLink, Link2, Linkedin } from "lucide-react";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import { ARTICLES } from "../data/content";
import { Reveal } from "../components/site/Reveal";
import { useSite } from "../context/SiteContext";

const ShareBar = ({ title }) => {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href : "";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="article-share-bar">
      <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Share</span>
      <a
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="article-share-linkedin"
        aria-label={`Share "${title}" on LinkedIn`}
        className="inline-flex items-center gap-2 rounded-full border border-slate-200 dark:border-white/10 px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:border-brand-sky hover:text-brand-navy dark:hover:text-brand-sky transition-colors duration-300"
      >
        <Linkedin size={13} /> LinkedIn
      </a>
      <button
        onClick={copy}
        data-testid="article-copy-link"
        className="inline-flex items-center gap-2 rounded-full border border-slate-200 dark:border-white/10 px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:border-brand-sky hover:text-brand-navy dark:hover:text-brand-sky transition-colors duration-300"
      >
        {copied ? <Check size={13} className="text-emerald-500" /> : <Link2 size={13} />}
        {copied ? "Copied" : "Copy link"}
      </button>
    </div>
  );
};

const Article = () => {
  const { slug } = useParams();
  const { openBooking } = useSite();
  const article = ARTICLES.find((a) => a.id === slug);

  useEffect(() => {
    if (article) document.title = `${article.title} — Medlyn 360`;
    return () => { document.title = "Medlyn 360 — Virtual Live Human Medical Scribing"; };
  }, [article]);

  if (!article) return <Navigate to="/resources" replace />;

  const related = ARTICLES.filter((a) => a.id !== article.id).slice(0, 3);

  return (
    <main data-testid="article-page">
      <Navbar />
      <section className="bg-gradient-to-br from-white via-brand-sky-soft/60 to-white dark:from-brand-ink dark:via-[#0A1030] dark:to-brand-ink pt-32 pb-12 md:pt-40 md:pb-16 relative overflow-hidden grain">
        <div className="absolute -top-24 right-0 w-96 h-96 rounded-full bg-brand-sky/15 blur-3xl" />
        <div className="relative max-w-3xl mx-auto px-6">
          <Reveal>
            <Link
              to="/resources"
              data-testid="article-back-link"
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 hover:text-brand-navy dark:hover:text-brand-sky transition-colors duration-300"
            >
              <ArrowLeft size={14} /> All Resources
            </Link>
          </Reveal>
          <Reveal delay={0.06}>
            <p className="mt-8 text-xs font-bold uppercase tracking-[0.25em] text-brand-sky" data-testid="article-category">{article.category}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-4 font-display text-3xl md:text-5xl font-bold tracking-tight text-slate-900 dark:text-white leading-[1.12]" data-testid="article-title">
              {article.title}
            </h1>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-5 flex items-center gap-2 text-sm text-slate-400" data-testid="article-meta">
              <Clock size={13} /> {article.readTime} · {article.date}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white dark:bg-brand-ink py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-6">
          <div className="space-y-6" data-testid="article-body">
            {article.body.map((p, i) => (
              <p key={i} className="text-base md:text-[17px] leading-[1.85] text-slate-600 dark:text-slate-300">{p}</p>
            ))}
          </div>

          <div className="mt-12 rounded-2xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/10 p-7" data-testid="article-sources">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Sources &amp; Citations</p>
            <ul className="space-y-2.5">
              {article.sources.map((s) => (
                <li key={s.url}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-navy dark:text-brand-sky hover:underline"
                    data-testid={`article-source-${s.name.split(" ")[0].toLowerCase()}`}
                  >
                    {s.name} <ExternalLink size={12} />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 rounded-2xl bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/10 p-5" data-testid="article-disclaimer">
            <p className="text-xs leading-relaxed text-slate-400">
              This article is provided for general informational purposes only and does not constitute medical, legal,
              coding, or billing advice. Figures referenced are drawn from published research and industry sources and
              may not reflect the results of any specific practice.
            </p>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6 border-t border-slate-100 dark:border-white/10 pt-8">
            <ShareBar title={article.title} />
            <button
              data-testid="article-book-call-button"
              onClick={openBooking}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-navy dark:bg-brand-sky text-white dark:text-brand-ink font-bold px-7 py-3.5 text-sm hover:shadow-[0_16px_40px_-14px_rgba(42,49,146,0.55)] transition-shadow duration-300"
            >
              Book a Discovery Call <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 dark:bg-[#0A1030] py-16 md:py-20" data-testid="article-related-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">Keep reading</h2>
          </Reveal>
          <div className="mt-8 grid md:grid-cols-3 gap-6">
            {related.map((a, i) => (
              <Reveal key={a.id} delay={i * 0.07}>
                <Link
                  to={`/resources/${a.id}`}
                  data-testid={`related-article-${a.id}`}
                  className="group block h-full rounded-3xl bg-white dark:bg-white/5 border border-slate-100 dark:border-white/10 p-7 hover:shadow-[0_24px_60px_-30px_rgba(42,49,146,0.35)] hover:-translate-y-1 transition-[transform,box-shadow] duration-300"
                >
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-brand-sky rounded-full bg-brand-sky/10 px-3 py-1.5">{a.category}</span>
                  <h3 className="mt-4 font-display text-base font-semibold text-slate-900 dark:text-white leading-snug group-hover:text-brand-navy dark:group-hover:text-brand-sky transition-colors duration-300">
                    {a.title}
                  </h3>
                  <p className="mt-4 flex items-center gap-2 text-[11px] text-slate-400">
                    <Clock size={11} /> {a.readTime} · {a.date}
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
};
export default Article;
